import React from 'react';
import { motion } from 'framer-motion';

const C = {
  cream: '#F5F1E8',
  gold: '#C6A96B',
  darkText: '#2C2C2C',
  darkTextSec: '#5A5A5A',
};

const fadeUp = {
  initial: { y: 20, opacity: 0 },
  animate: { y: 0, opacity: 1 },
  transition: { duration: 0.8, ease: [0.22, 1, 0.36, 1] },
};

const Section = ({ title, children }) => (
  <div className="mb-12">
    <h2 className="text-lg font-light mb-4" style={{ fontFamily: "'Cormorant Garamond', serif", color: C.darkText }}>{title}</h2>
    <div className="space-y-3 text-sm leading-relaxed" style={{ color: C.darkTextSec }}>{children}</div>
  </div>
);

const BulletList = ({ items }) => (
  <div className="space-y-2">
    {items.map((item, i) => (
      <div key={i} className="flex items-start gap-2.5">
        <div className="w-1 h-1 rounded-full mt-2 flex-shrink-0" style={{ background: `${C.gold}55` }} />
        <p className="text-sm leading-relaxed" style={{ color: C.darkTextSec }}>{item}</p>
      </div>
    ))}
  </div>
);

const LpPrivacyPage = () => (
  <div style={{ fontFamily: "'Manrope', sans-serif", background: C.cream, color: C.darkText, minHeight: '100vh' }}>
    <div className="max-w-2xl mx-auto px-6 md:px-12 py-20 md:py-28">
      <motion.div {...fadeUp}>
        <img
          src="/sent2ment-logo.png"
          alt="Sent2ment"
          className="h-16 w-auto object-contain mb-10"
        />

        <h1 className="text-3xl sm:text-4xl font-light tracking-tight mb-3" style={{ fontFamily: "'Cormorant Garamond', serif" }}>Privacy Policy</h1>
        <p className="text-sm italic mb-12" style={{ color: C.darkTextSec, fontFamily: "'Cormorant Garamond', serif" }}>Your privacy matters. What you share with us may be deeply personal, and we are committed to protecting your information with care, respect, and transparency.</p>

        <Section title="Who we are">
          <p>Sent2ment Ltd</p>
          <p>Email: <a href="mailto:hello@sent2ment.co.uk" className="transition-colors duration-300 hover:text-[#C6A96B]" style={{ color: C.gold }}>hello@sent2ment.co.uk</a></p>
          <p>Sent2ment Ltd is the data controller responsible for your personal data.</p>
        </Section>

        <Section title="What we collect">
          <p>When you use Sent2ment, we may collect:</p>
          <BulletList items={[
            'Your name and contact details (such as your email address)',
            'Order and purchase information',
            'Messages, written content, or personal notes you upload',
            'Audio recordings (including voice messages)',
            'Video recordings (if uploaded)',
            'Images or photographs you upload',
            'Recipient names (if provided)',
            'Information associated with your unique QR code and memory page',
          ]} />
        </Section>

        <Section title="How we use your information">
          <p>We use your information to:</p>
          <BulletList items={[
            'Process and fulfil your order',
            'Create, store, and manage your private Sent2ment memory',
            'Enable access to your content via your unique QR code',
            'Provide customer support',
            'Improve our website, services, and user experience',
            'Send essential service-related communications (such as order and upload confirmations)',
          ]} />
          <p className="mt-4 italic" style={{ fontFamily: "'Cormorant Garamond', serif", color: C.darkText }}>We do not use your personal memories, messages, images, audio, or video content for marketing or advertising purposes.</p>
          <p>We recognise that some content may be deeply personal or created during sensitive circumstances. All data is handled with discretion, care, and respect.</p>
        </Section>

        <Section title="Legal basis for processing">
          <p>We process your personal data in accordance with UK GDPR based on:</p>
          <BulletList items={[
            'Your consent when you upload and submit personal content',
            'The necessity to fulfil your order and provide our services',
            'Our legitimate interests in operating, securing, and improving our platform',
          ]} />
          <p>Where you upload content relating to another individual, you confirm that you have the appropriate consent or authority to do so.</p>
        </Section>

        <Section title="How your data is stored">
          <p>Your data is stored using secure, access-controlled systems and trusted cloud infrastructure.</p>
          <p>Each memory is linked to a unique QR code. Access is only possible through that QR code, authorised access, or any additional security features selected by the customer.</p>
          <p>We take appropriate technical and organisational measures to protect your data against unauthorised access, loss, misuse, or disclosure.</p>
        </Section>

        <Section title="Third-party services">
          <p>We use trusted third-party providers to support our services, including:</p>
          <BulletList items={[
            'Cloud storage and database providers',
            'Payment processors',
            'Email delivery providers',
            'Website analytics tools',
          ]} />
          <p>These providers process personal data only as necessary to perform their services and are required to handle data in accordance with data protection laws.</p>
        </Section>

        <Section title="International data transfers">
          <p>Some of our service providers may process data outside the United Kingdom. Where this occurs, we ensure appropriate safeguards are in place to protect your data in accordance with UK GDPR requirements.</p>
        </Section>

        <Section title="How long we keep your data">
          <BulletList items={[
            'Standard storage: 10 years',
            'Extended storage: 25 years (if selected at purchase)',
          ]} />
          <p className="mt-3">After the applicable storage period, your data may be permanently deleted.</p>
          <p>You may request deletion of your stored content at any time.</p>
        </Section>

        <Section title="Your rights">
          <p>Under UK GDPR, you have the right to:</p>
          <BulletList items={[
            'Request access to your personal data',
            'Request correction of inaccurate data',
            'Request deletion of your data',
            'Restrict or object to processing',
            'Withdraw consent where processing is based on consent',
          ]} />
          <p className="mt-3">To exercise your rights, contact: <a href="mailto:hello@sent2ment.co.uk" className="transition-colors duration-300 hover:text-[#C6A96B]" style={{ color: C.gold }}>hello@sent2ment.co.uk</a></p>
        </Section>

        <Section title="Deleting your data">
          <p>You may request deletion of your content at any time. Once deleted, your data cannot be recovered.</p>
        </Section>

        <Section title="Cookies">
          <p>We use cookies and similar technologies to support website functionality, improve performance, and understand how visitors use our site. Where required, you will be given the option to manage your cookie preferences.</p>
        </Section>

        <Section title="Updates to this policy">
          <p>We may update this Privacy Policy from time to time. Any changes will be published on this page.</p>
        </Section>

        <div className="border-t pt-8 mt-16 text-center" style={{ borderColor: `${C.gold}15` }}>
          <p className="text-xs italic mb-3" style={{ fontFamily: "'Cormorant Garamond', serif", color: C.gold }}>Sent2ment Ltd — protecting what matters most.</p>
          <p className="text-[11px] leading-relaxed mb-2" style={{ color: `${C.darkTextSec}AA` }}>
            Sent2ment<sup style={{ fontSize: '0.7em', verticalAlign: 'super' }}>®</sup> is a registered trademark in the United Kingdom.
          </p>
          <p className="text-[11px] leading-relaxed" style={{ color: `${C.darkTextSec}AA` }}>
            All content, product concepts, and digital memory systems are the intellectual property of Sent2ment Ltd.
          </p>
        </div>
      </motion.div>
    </div>
  </div>
);

export default LpPrivacyPage;
